import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const LABELS = { clients: "Clients", quotes: "Quotes", jobs: "Jobs" };

export default function ImportDialog({ open, onClose, kind, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [onConflict, setOnConflict] = useState("skip");

  const reset = () => { setFile(null); setResult(null); setBusy(false); setOnConflict("skip"); };
  const close = () => { reset(); onClose(); };

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("on_conflict", onConflict);
      const res = await api.importCsv(kind, fd);
      setResult(res);
      if (res.created > 0 || res.merged > 0) { toast.success(`Imported ${res.created}${res.merged ? `, updated ${res.merged}` : ""} ${kind}`); onDone && onDone(); }
      else toast.warning("No rows imported");
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" className="text-[#00B4D8]" /> Import {LABELS[kind]}
          </DialogTitle>
          <DialogDescription>Upload a CSV to bulk-add {kind}. Download the template to see the exact columns.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <a href={api.importTemplateUrl(kind)} className="inline-flex items-center gap-1.5 text-sm text-[#00B4D8] hover:underline" data-testid="import-template">
            <DownloadSimple size={16} /> Download {kind} template
          </a>

          <div>
            <input
              type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
              data-testid="import-file"
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#0B1726] file:text-white file:text-sm file:cursor-pointer hover:file:bg-slate-800"
            />
            {kind !== "clients" && <p className="text-xs text-slate-400 mt-2">Clients are matched by email (or name). Rows with unknown clients are skipped.</p>}
          </div>

          {kind === "clients" && (
            <div data-testid="import-conflict-mode">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">When a client already exists (matched by email or phone)</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setOnConflict("skip")} data-testid="conflict-skip"
                  className={`text-left rounded-lg border p-3 transition-colors duration-200 ${onConflict === "skip" ? "border-[#00B4D8] bg-cyan-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-sm font-semibold text-slate-800">Skip duplicates</div>
                  <div className="text-xs text-slate-500 mt-0.5">Leave existing clients untouched</div>
                </button>
                <button type="button" onClick={() => setOnConflict("merge")} data-testid="conflict-merge"
                  className={`text-left rounded-lg border p-3 transition-colors duration-200 ${onConflict === "merge" ? "border-[#00B4D8] bg-cyan-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="text-sm font-semibold text-slate-800">Merge &amp; enrich</div>
                  <div className="text-xs text-slate-500 mt-0.5">Fill blanks, add tags &amp; new addresses</div>
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-emerald-600 font-semibold"><CheckCircle size={18} weight="fill" /> {result.created} imported</div>
              {result.merged > 0 && (
                <div className="flex items-center gap-1.5 text-[#00B4D8] font-semibold" data-testid="import-merged">
                  <CheckCircle size={16} weight="fill" /> {result.merged} existing updated
                </div>
              )}
              {result.skipped > 0 && (
                <div className="flex items-center gap-1.5 text-slate-500" data-testid="import-skipped">
                  <WarningCircle size={16} /> {result.skipped} skipped (duplicates or archived)
                </div>
              )}
              {result.error_count > 0 && (
                <div className="text-amber-700">
                  <div className="flex items-center gap-1.5 font-semibold"><WarningCircle size={16} /> {result.error_count} skipped</div>
                  <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-slate-500 list-disc pl-5">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>Close</Button>
          <Button onClick={upload} disabled={busy || !file} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="import-submit">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
